import GeoJSON from 'geojson'

import { Coordinates } from '@/domain/protocols/Coordinates'

import { ObstacleFeatureInfoData } from '../../states/FeatureInfoState'

import { TranslationTokens } from '@/i18n/ITranslations'
import { convertDecimalDegreesToDMS } from '@/utils/coordinates'

const OBSTACLE_TYPE_TRANSLATION_TOKENS: Record<string, TranslationTokens> = {
  AG_EQUIP: 'MAP_OBSTACLES_TYPE_AG_EQUIP',
  ANTENNA: 'MAP_OBSTACLES_TYPE_ANTENNA',
  ARCH: 'MAP_OBSTACLES_TYPE_ARCH',
  BRIDGE: 'MAP_OBSTACLES_TYPE_BRIDGE',
  BUILDING: 'MAP_OBSTACLES_TYPE_BUILDING',
  CABLE_CAR: 'MAP_OBSTACLES_TYPE_CABLE_CAR',
  CATENARY: 'MAP_OBSTACLES_TYPE_CATENARY',
  COMPRESSED_AIR_SYSTEM: 'MAP_OBSTACLES_TYPE_COMPRESSED_AIR_SYSTEM',
  CONTROL_MONITORING_SYSTEM: 'MAP_OBSTACLES_TYPE_CONTROL_MONITORING_SYSTEM',
  CONTROL_TOWER: 'MAP_OBSTACLES_TYPE_CONTROL_TOWER',
  COOLING_TOWER: 'MAP_OBSTACLES_TYPE_COOLING_TOWER',
  CRANE: 'MAP_OBSTACLES_TYPE_CRANE',
  DAM: 'MAP_OBSTACLES_TYPE_DAM',
  DOME: 'MAP_OBSTACLES_TYPE_DOME',
  ELECTRICAL_EXIT_LIGHT: 'MAP_OBSTACLES_TYPE_ELECTRICAL_EXIT_LIGHT',
  ELECTRICAL_SYSTEM: 'MAP_OBSTACLES_TYPE_ELECTRICAL_SYSTEM',
  ELEVATOR: 'MAP_OBSTACLES_TYPE_ELEVATOR',
  FENCE: 'MAP_OBSTACLES_TYPE_FENCE',
  FUEL_SYSTEM: 'MAP_OBSTACLES_TYPE_FUEL_SYSTEM',
  GATE: 'MAP_OBSTACLES_TYPE_GATE',
  GENERAL_UTILITY: 'MAP_OBSTACLES_TYPE_GENERAL_UTILITY',
  GRAIN_ELEVATOR: 'MAP_OBSTACLES_TYPE_GRAIN_ELEVATOR',
  HEAT_COOL_SYSTEM: 'MAP_OBSTACLES_TYPE_HEAT_COOL_SYSTEM',
  INDUSTRIAL_SYSTEM: 'MAP_OBSTACLES_TYPE_INDUSTRIAL_SYSTEM',
  LIGHTHOUSE: 'MAP_OBSTACLES_TYPE_LIGHTHOUSE',
  MONUMENT: 'MAP_OBSTACLES_TYPE_MONUMENT',
  NATURAL_GAS_SYSTEM: 'MAP_OBSTACLES_TYPE_NATURAL_GAS_SYSTEM',
  NATURAL_HIGHPOINT: 'MAP_OBSTACLES_TYPE_NATURAL_HIGHPOINT',
  NAVAID: 'MAP_OBSTACLES_TYPE_NAVAID',
  NUCLEAR_REACTOR: 'MAP_OBSTACLES_TYPE_NUCLEAR_REACTOR',
  OTHER: 'MAP_OBSTACLES_TYPE_OTHER',
  POLE: 'MAP_OBSTACLES_TYPE_POLE',
  POWER_PLANT: 'MAP_OBSTACLES_TYPE_POWER_PLANT',
  REFINERY: 'MAP_OBSTACLES_TYPE_REFINERY',
  RIG: 'MAP_OBSTACLES_TYPE_RIG',
  SALTWATER_SYSTEM: 'MAP_OBSTACLES_TYPE_SALTWATER_SYSTEM',
  SIGN: 'MAP_OBSTACLES_TYPE_SIGN',
  SPIRE: 'MAP_OBSTACLES_TYPE_SPIRE',
  STACK: 'MAP_OBSTACLES_TYPE_STACK',
  STADIUM: 'MAP_OBSTACLES_TYPE_STADIUM',
  STORM_SYSTEM: 'MAP_OBSTACLES_TYPE_STORM_SYSTEM',
  TANK: 'MAP_OBSTACLES_TYPE_TANK',
  TETHERED_BALLOON: 'MAP_OBSTACLES_TYPE_TETHERED_BALLOON',
  TOWER: 'MAP_OBSTACLES_TYPE_TOWER',
  TRAMWAY: 'MAP_OBSTACLES_TYPE_TRAMWAY',
  TRANSMISSION_LINE: 'MAP_OBSTACLES_TYPE_TRANSMISSION_LINE',
  TREE: 'MAP_OBSTACLES_TYPE_TREE',
  URBAN: 'MAP_OBSTACLES_TYPE_URBAN',
  VEGETATION: 'MAP_OBSTACLES_TYPE_VEGETATION',
  WALL: 'MAP_OBSTACLES_TYPE_WALL',
  WASTEWATER_SYSTEM: 'MAP_OBSTACLES_TYPE_WASTEWATER_SYSTEM',
  WATER_SYSTEM: 'MAP_OBSTACLES_TYPE_WATER_SYSTEM',
  WATER_TOWER: 'MAP_OBSTACLES_TYPE_WATER_TOWER',
  WINDMILL: 'MAP_OBSTACLES_TYPE_WINDMILL',
  WINDMILL_FARMS: 'MAP_OBSTACLES_TYPE_WINDMILL_FARMS'
}

type ObstacleTypeToken =
  | (typeof OBSTACLE_TYPE_TRANSLATION_TOKENS)[keyof typeof OBSTACLE_TYPE_TRANSLATION_TOKENS]
  | 'MAP_OBSTACLE_INFO_FALLBACK-TITLE'

export function getObstacleTypeToken(type: string | undefined): ObstacleTypeToken {
  if (!type) return 'MAP_OBSTACLE_INFO_FALLBACK-TITLE'
  return OBSTACLE_TYPE_TRANSLATION_TOKENS[type] ?? 'MAP_OBSTACLE_INFO_FALLBACK-TITLE'
}

const FT_PER_M = 0.3048

function isPositiveNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isOptionalNumber(value: unknown): value is number | undefined {
  return value === undefined || value === null || (typeof value === 'number' && Number.isFinite(value))
}

export function formatAltitudeTopFt(elevationTopFt: unknown, unknownLabel: string): string {
  if (!isPositiveNumber(elevationTopFt)) return unknownLabel
  return `${Math.round(elevationTopFt)} ft`
}

export function formatObstacleHeight(elevationTopFt: unknown, elevationBaseFt: unknown, unknownLabel: string): string {
  if (!isPositiveNumber(elevationTopFt) || !isPositiveNumber(elevationBaseFt)) return unknownLabel
  if (elevationBaseFt <= 0) return unknownLabel
  if (elevationTopFt < elevationBaseFt) return unknownLabel

  const heightFt = elevationTopFt - elevationBaseFt
  const heightM = heightFt * FT_PER_M

  return `${Math.round(heightM)} m (${Math.round(heightFt)} ft)`
}

export function formatLighted(lighted: unknown, yesLabel: string, noLabel: string, unknownLabel: string): string {
  if (typeof lighted !== 'boolean') return unknownLabel
  return lighted ? yesLabel : noLabel
}

export function formatObstacleCoordinates(coordinates: Coordinates): string {
  return convertDecimalDegreesToDMS(coordinates.latitude, coordinates.longitude)
}

export function projectObstacleFeature(
  feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> | null | undefined
): ObstacleFeatureInfoData | null {
  if (!feature || !feature.properties) return null

  const properties = feature.properties

  const obstacleId = properties.obstacleId
  if (typeof obstacleId !== 'string' || obstacleId.length === 0) return null

  if (!feature.geometry || feature.geometry.type !== 'Point') return null

  const [longitude, latitude] = feature.geometry.coordinates ?? []
  if (typeof longitude !== 'number' || typeof latitude !== 'number') return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null

  if (!isOptionalNumber(properties.elevationTopFt)) return null
  if (!isOptionalNumber(properties.elevationBaseFt)) return null

  const type = typeof properties.type === 'string' && properties.type.trim().length > 0 ? properties.type : undefined
  const lighted = typeof properties.lighted === 'boolean' ? properties.lighted : undefined

  return {
    obstacleId,
    type,
    elevationTopFt: typeof properties.elevationTopFt === 'number' ? properties.elevationTopFt : undefined,
    elevationBaseFt: typeof properties.elevationBaseFt === 'number' ? properties.elevationBaseFt : undefined,
    lighted,
    coordinates: Coordinates.create({ latitude, longitude })
  }
}
